export interface GridPointPlacementObject {
  t: string;
  cx?: number | string;
  cy?: number | string;
  color?: string;
  high?: boolean;
  [key: string]: unknown;
}

interface ApplyGridPointPlacementOptions<
  T extends GridPointPlacementObject = GridPointPlacementObject
> {
  actingAsNumberLineGetter?: boolean;
  inactivePointColor: string;
  multiInput?: boolean;
  objects: T[];
  point: T & { cx: number; cy: number };
}

export interface GridPointPlacementResult {
  action: 'added' | 'removed';
}

const hasMatchingCoordinates = (
  object: GridPointPlacementObject,
  point: { cx: number; cy: number }
): boolean => Number(object.cx) === point.cx && Number(object.cy) === point.cy;

/**
 * Adds or removes a student point while preserving the grid getter's point-entry rules.
 * Number-line-backed grid getters replace prior dots because their answer processors expect one point.
 */
export const applyGridPointPlacement = <T extends GridPointPlacementObject>({
  actingAsNumberLineGetter,
  inactivePointColor,
  multiInput,
  objects,
  point,
}: ApplyGridPointPlacementOptions<T>): GridPointPlacementResult => {
  if (actingAsNumberLineGetter) {
    const hasMatchingPoint = objects.some(
      obj => obj.t === 'dot' && hasMatchingCoordinates(obj, point)
    );

    for (let index = objects.length - 1; index >= 0; index--) {
      if (objects[index].t === 'dot') {
        objects.splice(index, 1);
      }
    }

    if (hasMatchingPoint) {
      return { action: 'removed' };
    }
  } else {
    for (let index = 0; index < objects.length; index++) {
      const object = objects[index];

      if (object.t !== 'dot') {
        continue;
      }

      if (hasMatchingCoordinates(object, point)) {
        objects.splice(index, 1);

        return { action: 'removed' };
      }

      if (!multiInput && !object.high) {
        object.color = inactivePointColor;
      }
    }
  }

  objects.push(point);

  return { action: 'added' };
};
